import { useMemo } from 'react';

export type SpectrogramPanelProps = {
  /**
   * 2D amplitude grid. Outer index = time bin (left → right), inner
   * index = frequency bin (bottom → top). Values are normalized
   * internally — caller can pass any non-negative scale.
   */
  grid: number[][];
  /** Maximum frequency at the top edge of the chart. */
  maxHz: number;
  /** Time window in seconds the grid spans (used for x-axis label). */
  durationSec: number;
  /** Color stop at peak amplitude. Defaults to "critical". */
  peakTone?: 'info' | 'watch' | 'warn' | 'critical';
  state?: 'default' | 'loading';
  /** LinkedCursor position as a fraction (0..1) of the visible window. */
  cursorFraction?: number | null;
};

const VIEW_W = 600;
const VIEW_H = 220;
// Asymmetric padding leaves room for axis ticks/labels:
//   left  — frequency tick labels + "Hz" axis title
//   bottom — time tick labels + "s" axis title
//   right — amplitude legend strip
//   top   — minimal headroom
const PAD_TOP = 14;
const PAD_RIGHT = 56;
const PAD_BOTTOM = 36;
const PAD_LEFT = 50;

const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;

const TONE_VAR: Record<NonNullable<SpectrogramPanelProps['peakTone']>, string> = {
  info: 'var(--color-info)',
  watch: 'var(--color-watch)',
  warn: 'var(--color-warn)',
  critical: 'var(--color-critical)',
};

/**
 * Spectrogram heatmap — time (x) × frequency (y) × amplitude (color).
 * Each cell is an SVG <rect> tinted by `peakTone` with opacity
 * proportional to the cell's amplitude relative to the grid's peak.
 * Pure SVG (no canvas) so the chart stays accessible and copy-friendly
 * at the cell sizes that matter for fault diagnosis (≤ 128×128).
 *
 * Axes carry the diagnostic burden: without labelled ticks, an
 * operator can't tell *which* frequency a bright horizontal band sits
 * at — and that's the whole point of the view. Y-axis is frequency in
 * Hz, X-axis is time in seconds, both with nice round tick marks.
 * A small amplitude gradient on the right makes the color→amplitude
 * mapping explicit.
 */
export function SpectrogramPanel({
  grid,
  maxHz,
  durationSec,
  peakTone = 'critical',
  state = 'default',
  cursorFraction = null,
}: SpectrogramPanelProps) {
  const { cells, peak } = useMemo(() => buildCells(grid), [grid]);

  if (state === 'loading' || grid.length === 0) {
    return (
      <div
        role="img"
        aria-label="Spectrogram loading"
        aria-busy={state === 'loading'}
        className="flex h-50 items-center justify-center rounded-r-card border border-gray-300 bg-gray-50 text-fs-meta text-gray-700"
        style={{ minHeight: '12rem' }}
      >
        {state === 'loading' ? 'Computing spectrogram…' : 'No spectrogram data'}
      </div>
    );
  }

  const a11yLabel = `Spectrogram: ${grid.length} time bins × ${grid[0]?.length ?? 0} frequency bins, 0–${maxHz} Hz over ${durationSec.toFixed(1)} s, peak ${peak.toFixed(2)}`;
  const fill = TONE_VAR[peakTone];

  const freqTicks = useMemo(() => niceTicks(maxHz, 5), [maxHz]);
  const timeTicks = useMemo(() => niceTicks(durationSec, 5), [durationSec]);
  const gradientId = useMemo(() => `spec-grad-${peakTone}`, [peakTone]);

  const yForFreq = (hz: number) => PAD_TOP + (1 - hz / maxHz) * PLOT_H;
  const xForTime = (s: number) => PAD_LEFT + (s / durationSec) * PLOT_W;

  return (
    <figure className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-3">
      <svg
        role="img"
        aria-label={a11yLabel}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="block w-full"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor={fill} stopOpacity="0" />
            <stop offset="1" stopColor={fill} stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Plot background */}
        <rect
          x={PAD_LEFT}
          y={PAD_TOP}
          width={PLOT_W}
          height={PLOT_H}
          fill="var(--color-gray-100)"
        />

        {/* Heatmap cells */}
        {cells.map((c, i) => (
          <rect
            key={i}
            x={c.x}
            y={c.y}
            width={c.w}
            height={c.h}
            fill={fill}
            fillOpacity={c.opacity}
          />
        ))}

        {/* Frequency axis (left): ticks + labels + "Hz" title */}
        {freqTicks.map((hz) => {
          const y = yForFreq(hz);
          return (
            <g key={`fy-${hz}`}>
              <line
                x1={PAD_LEFT - 4}
                y1={y}
                x2={PAD_LEFT}
                y2={y}
                stroke="var(--color-gray-700)"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 7}
                y={y + 3}
                textAnchor="end"
                fontFamily="ui-monospace, monospace"
                fontSize="10"
                fill="var(--color-gray-900)"
              >
                {Math.round(hz)}
              </text>
            </g>
          );
        })}
        <text
          x={12}
          y={PAD_TOP + PLOT_H / 2}
          textAnchor="middle"
          fontFamily="ui-monospace, monospace"
          fontSize="10"
          fill="var(--color-gray-700)"
          transform={`rotate(-90, 12, ${PAD_TOP + PLOT_H / 2})`}
        >
          Hz
        </text>

        {/* Time axis (bottom): ticks + labels + "s" title */}
        {timeTicks.map((s) => {
          const x = xForTime(s);
          return (
            <g key={`tx-${s}`}>
              <line
                x1={x}
                y1={PAD_TOP + PLOT_H}
                x2={x}
                y2={PAD_TOP + PLOT_H + 4}
                stroke="var(--color-gray-700)"
                strokeWidth={1}
              />
              <text
                x={x}
                y={PAD_TOP + PLOT_H + 16}
                textAnchor="middle"
                fontFamily="ui-monospace, monospace"
                fontSize="10"
                fill="var(--color-gray-900)"
              >
                {formatTime(s, durationSec)}
              </text>
            </g>
          );
        })}
        <text
          x={PAD_LEFT + PLOT_W / 2}
          y={VIEW_H - 6}
          textAnchor="middle"
          fontFamily="ui-monospace, monospace"
          fontSize="10"
          fill="var(--color-gray-700)"
        >
          time (s)
        </text>

        {/* Amplitude legend (right): gradient strip + low/peak labels */}
        {(() => {
          const legX = VIEW_W - PAD_RIGHT + 14;
          const legW = 8;
          return (
            <g>
              <rect
                x={legX}
                y={PAD_TOP}
                width={legW}
                height={PLOT_H}
                fill={`url(#${gradientId})`}
                stroke="var(--color-gray-300)"
                strokeWidth={0.5}
              />
              <text
                x={legX + legW + 3}
                y={PAD_TOP + 8}
                fontFamily="ui-monospace, monospace"
                fontSize="9"
                fill="var(--color-gray-900)"
              >
                peak
              </text>
              <text
                x={legX + legW + 3}
                y={PAD_TOP + PLOT_H - 1}
                fontFamily="ui-monospace, monospace"
                fontSize="9"
                fill="var(--color-gray-700)"
              >
                low
              </text>
            </g>
          );
        })()}

        {/* Linked cursor overlay */}
        {cursorFraction != null ? (() => {
          const f = Math.max(0, Math.min(1, cursorFraction));
          const cx = PAD_LEFT + f * PLOT_W;
          return (
            <line
              x1={cx}
              y1={PAD_TOP}
              x2={cx}
              y2={PAD_TOP + PLOT_H}
              stroke="var(--color-warn)"
              strokeWidth={1.4}
              strokeDasharray="4 2"
            />
          );
        })() : null}
      </svg>
    </figure>
  );
}

/**
 * Pick "nice" round tick values from 0 to max. Aims for ~targetCount
 * ticks but adjusts to keep the step size at a human-friendly value
 * (1/2/5 × 10ⁿ). Always includes 0 and a tick at-or-just-past max.
 */
function niceTicks(max: number, targetCount: number): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const rawStep = max / targetCount;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let step: number;
  if (norm < 1.5) step = mag;
  else if (norm < 3) step = 2 * mag;
  else if (norm < 7) step = 5 * mag;
  else step = 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 1e-6; v += step) {
    ticks.push(Number(v.toFixed(10)));
  }
  return ticks;
}

/** Format axis tick — fractional seconds when the window is short. */
function formatTime(value: number, duration: number): string {
  if (duration >= 10) return `${value.toFixed(0)}`;
  if (duration >= 1) return `${value.toFixed(1)}`;
  return value.toFixed(2);
}

function buildCells(grid: number[][]) {
  const tBins = grid.length;
  const fBins = grid[0]?.length ?? 0;
  let peak = 0;
  for (let t = 0; t < tBins; t++) {
    const col = grid[t];
    for (let f = 0; f < fBins; f++) {
      const v = col[f];
      if (v > peak) peak = v;
    }
  }
  if (peak === 0) peak = 1;
  const cellW = PLOT_W / Math.max(1, tBins);
  const cellH = PLOT_H / Math.max(1, fBins);
  const cells: { x: number; y: number; w: number; h: number; opacity: number }[] = [];
  for (let t = 0; t < tBins; t++) {
    const col = grid[t];
    for (let f = 0; f < fBins; f++) {
      const v = col[f];
      if (v <= 0) continue;
      const opacity = Math.min(1, v / peak);
      if (opacity < 0.04) continue; // skip nearly-invisible cells
      cells.push({
        x: PAD_LEFT + t * cellW,
        // Frequency 0 is at the BOTTOM; row 0 of the array is the
        // lowest band, so rows count up from the bottom.
        y: PAD_TOP + (fBins - 1 - f) * cellH,
        w: cellW + 0.4, // slight overlap kills hairline gaps
        h: cellH + 0.4,
        opacity,
      });
    }
  }
  return { cells, peak };
}
